import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { receiptDocumentPath } from '../../api/receipt.api';
import { DownloadLabel } from '../../components/detail/DownloadLabel';
import { AddIcNumberSheet } from '../../components/donation/AddIcNumberSheet';
import { TaxStatusTag } from '../../components/donation/TaxStatusTag';
import { Button } from '../../components/ui/button';
import { DONATION_TONE } from '../../constants/donation.const';
import { useDonationReceiptDownload } from '../../hooks/donations/useDonationReceiptDownload';
import {
  useCharityDonations,
  useCharityYearDetail
} from '../../hooks/donations/useDonations';
import { useDebounce } from '../../hooks/useDebounce';
import { useDownloadInFlight } from '../../hooks/useDownloadInFlight';
import { useScrollToTopOnOpen } from '../../hooks/useScrollToTopOnOpen';
import BackHeader from '../../shared/BackHeader';
import type {
  CharityYearDetail,
  DonationLine,
  DonationMonthTotals
} from '../../types/donation.types';
import { formatDonationMonth, pluralize } from '../../utils/donation-format';
import { formatCurrency } from '../../utils/fiat-currency.util';
import { DonationLineRow } from './components/DonationLineRow';
import { LoadingState, MessageState } from './components/PageStates';

const SEARCH_DEBOUNCE_MS = 300;

/** Loaded donations grouped by calendar month, keeping their newest-first order. */
const groupByMonth = (donations: DonationLine[]) => {
  const groups: { month: string; items: DonationLine[] }[] = [];
  for (const donation of donations) {
    const month = dayjs(donation.date).format('YYYY-MM');
    const last = groups[groups.length - 1];
    if (last?.month === month) last.items.push(donation);
    else groups.push({ month, items: [donation] });
  }
  return groups;
};

function MonthHeader({
  month,
  totals,
  currency
}: {
  month: string;
  totals?: DonationMonthTotals;
  currency: string;
}) {
  return (
    <div className="flex items-baseline justify-between border-b border-gray-200 pb-2 pt-5">
      <h2 className="text-xs font-semibold uppercase tracking-widest text-gray-500">
        {formatDonationMonth(month)}
      </h2>
      {totals && (
        <p className="text-xs text-gray-500">
          {pluralize(totals.donation_count, 'donation', 'donations')} ·{' '}
          {formatCurrency(totals.total, currency)}
        </p>
      )}
    </div>
  );
}

const CharityDonationsPage: React.FC = () => {
  useScrollToTopOnOpen();
  const { charityId = '' } = useParams<{ charityId: string }>();
  const [searchParams] = useSearchParams();
  const year = Number(searchParams.get('year')) || new Date().getFullYear();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const applySearch = useDebounce<string>(setSearch, SEARCH_DEBOUNCE_MS);

  const detail = useCharityYearDetail(charityId, year);
  const donations = useCharityDonations(charityId, year, search);
  const receipt = useDonationReceiptDownload();
  const bundlePreparing = useDownloadInFlight(
    receiptDocumentPath.charityBundle(charityId, year)
  );

  if (detail.isLoading) {
    return (
      <div className="min-h-screen bg-white">
        <BackHeader />
        <LoadingState />
      </div>
    );
  }

  if (detail.isError || !detail.data) {
    return (
      <div className="min-h-screen bg-white">
        <BackHeader />
        <MessageState>
          We couldn't load these donations. Please try again later.
        </MessageState>
      </div>
    );
  }

  const yearDetail: CharityYearDetail = detail.data;
  const { charity, currency } = yearDetail;
  const loaded = donations.data?.pages.flatMap((page) => page.items) ?? [];
  const totalCount = donations.data?.pages[0]?.total_count ?? 0;
  const monthTotals = new Map(yearDetail.months.map((m) => [m.month, m]));

  return (
    <div className="min-h-screen bg-white">
      <BackHeader title={charity.name} />
      <div className="mx-auto max-w-2xl px-4 py-4 pb-10">
        <section
          aria-label={`${charity.name} in ${year}`}
          className={clsx(
            'rounded-3xl border p-5 shadow-sm',
            DONATION_TONE.card
          )}
        >
          <div className="flex flex-wrap items-center gap-2">
            <TaxStatusTag taxDeductible={charity.tax_deductible} />
            {charity.registration_no && (
              <span className="text-xs text-gray-600">
                Reg. no. {charity.registration_no}
              </span>
            )}
          </div>
          <div className="mt-3 flex items-baseline justify-between gap-3">
            <p className="text-sm text-gray-600">
              {year} ·{' '}
              {pluralize(yearDetail.donation_count, 'donation', 'donations')}
            </p>
            <p className={clsx('text-2xl font-bold', DONATION_TONE.text)}>
              {formatCurrency(yearDetail.total, currency)}
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              receipt.downloadAll(charity.id, year, charity.tax_deductible)
            }
            disabled={bundlePreparing}
            aria-busy={bundlePreparing}
            className={clsx(
              'mt-4 h-12 w-full rounded-xl text-sm font-semibold',
              DONATION_TONE.outlineButton
            )}
          >
            <DownloadLabel pending={bundlePreparing}>
              Download all{' '}
              {pluralize(yearDetail.donation_count, 'receipt', 'receipts')}{' '}
              (PDF)
            </DownloadLabel>
          </Button>
          {!charity.tax_deductible && (
            <p className="mt-3 text-xs text-gray-600">
              Receipts are for your records. They can't be used for tax relief.
            </p>
          )}
        </section>

        <div className="relative mt-4">
          <MagnifyingGlassIcon
            className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
            aria-hidden="true"
          />
          <input
            type="search"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              applySearch(e.target.value);
            }}
            placeholder="Search store or receipt no."
            aria-label="Search store or receipt no."
            className="block w-full rounded-full border border-gray-200 py-3 pl-11 pr-4 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#5CB891] focus:outline-none focus:ring-1 focus:ring-[#5CB891]"
          />
        </div>

        {donations.isLoading ? (
          <LoadingState />
        ) : donations.isError ? (
          <MessageState>
            We couldn't load these donations. Please try again later.
          </MessageState>
        ) : loaded.length === 0 ? (
          <MessageState>
            {search
              ? `No donations match “${search}”.`
              : `No donations to ${charity.name} in ${year}.`}
          </MessageState>
        ) : (
          <>
            {groupByMonth(loaded).map((group) => (
              <section key={group.month}>
                <MonthHeader
                  month={group.month}
                  // A month's figures cover every donation in it, so a search hides them.
                  totals={search ? undefined : monthTotals.get(group.month)}
                  currency={currency}
                />
                <ul className="divide-y divide-gray-100">
                  {group.items.map((donation) => (
                    <DonationLineRow
                      key={donation.donation_ref}
                      donation={donation}
                      primary={donation.store_name}
                      secondary={`${dayjs(donation.date).format('DD MMM, HH:mm')} · ${donation.donation_ref}`}
                      onDownloadReceipt={(d) =>
                        receipt.download(d.donation_ref, d.deductible_for_donor)
                      }
                    />
                  ))}
                </ul>
              </section>
            ))}

            <div className="mt-6 flex flex-col items-center gap-3">
              <p className="text-xs text-gray-500">
                Showing {loaded.length} of{' '}
                {pluralize(totalCount, 'donation', 'donations')}
              </p>
              {donations.hasNextPage && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => donations.fetchNextPage()}
                  disabled={donations.isFetchingNextPage}
                  className="rounded-full px-6"
                >
                  {donations.isFetchingNextPage ? 'Loading...' : 'Load more'}
                </Button>
              )}
            </div>
          </>
        )}
      </div>

      <AddIcNumberSheet {...receipt.sheet} />
    </div>
  );
};

export default CharityDonationsPage;
